// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides API functions for managing organization-level ledger
 * account defaults. These accounts are used for tracking inventory and fixed assets
 * throughout their lifecycle in the accounting system.
 */
import { LedgerAccounts, ledgerAccountsSchema } from '@/account/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { z } from 'zod';

/**
 * Schema defining the response structure for ledger accounts API calls.
 */
const ledgerAccountsResponseSchema = z.object({
  data: ledgerAccountsSchema,
  metadata: responseMetadataSchema,
});

/**
 * Retrieves the ledger account defaults for an organization.
 * Returns all 11 ledger account types with their configured values (or null if not set).
 *
 * @param orgKey - The unique key identifying the organization
 * @returns Promise resolving to the ledger accounts configuration
 * @throws Error if the API request fails or response validation fails
 */
export async function getOrganizationLedgerAccounts(
  orgKey: string
): Promise<LedgerAccounts> {
  const path = `/v1/organization/${orgKey}/accounting`;
  const response = await api.get(path);
  const result = await parseResponse(
    ledgerAccountsResponseSchema,
    response.data,
    {
      schemaName: 'OrganizationLedgerAccounts',
      endpoint: path,
    }
  );
  return result.data;
}

/**
 * Schema for validating the API response
 */
const successResponseSchema = z.object({
  data: z.object({ success: z.boolean() }),
  metadata: responseMetadataSchema,
});

/**
 * Updates the ledger account defaults for an organization.
 * All 11 account types must be provided in the request, even if unchanged.
 *
 * @param orgKey - The unique key identifying the organization
 * @param ledgerAccounts - The complete ledger accounts configuration to save
 * @returns Promise resolving when the update is successful
 * @throws Error if the API request fails or validation fails
 */
export async function updateOrganizationLedgerAccounts(
  orgKey: string,
  ledgerAccounts: LedgerAccounts
) {
  const path = `/v1/organization/${orgKey}/accounting`;

  const response = await api.patch(path, ledgerAccounts);
  const result = await parseResponse(successResponseSchema, response.data, {
    schemaName: 'UpdateOrganizationLedgerAccounts',
    endpoint: path,
  });
  return result.data;
}
